import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import store from "./store";
import App from "./App";
import AuthProvider from "./components/auth/AuthProvider";
import { devLog } from "./utils/devLog";
import "./index.css";

const rawPublishableKey = String(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "").trim();
const publishableKey = rawPublishableKey;

if (!publishableKey.startsWith("pk_")) {
  throw new Error("Missing Publishable Key");
}

const clerkJsUrl =
  String(import.meta.env.VITE_CLERK_JS_URL || "").trim() ||
  (import.meta.env.DEV ? "https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5/dist/clerk.browser.js" : "");

// Clerk configuration
const clerkConfig = {
  signInUrl: "/agency/sign-in",
  signUpUrl: "/agency/sign-up",
  signInForceRedirectUrl: "/agency/dashboard",
  signInFallbackRedirectUrl: "/agency/dashboard",
  signUpFallbackRedirectUrl: "/agency/dashboard",
  ...(clerkJsUrl ? { clerkJSUrl: clerkJsUrl } : {}),
};

// Debug logging for Clerk events
const logClerkEvent = (event) => {
  devLog("clerk", "event", {
    type: event.type,
    userId: event.data?.userId,
    email: event.data?.email,
  });
};

devLog("app", "startup", {
  hasPublishableKey: Boolean(publishableKey),
  clerkJsUrl: clerkJsUrl || "instance-default",
  signInUrl: clerkConfig.signInUrl,
  signUpUrl: clerkConfig.signUpUrl,
  signInForceRedirectUrl: clerkConfig.signInForceRedirectUrl,
  signInFallbackRedirectUrl: clerkConfig.signInFallbackRedirectUrl,
  signUpFallbackRedirectUrl: clerkConfig.signUpFallbackRedirectUrl,
});

if (import.meta.env.DEV && typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    devLog("app", "window_error", {
      path: window.location.pathname,
      search: window.location.search,
      message: String(event.message || ""),
      source: String(event.filename || ""),
      line: Number(event.lineno || 0),
      column: Number(event.colno || 0),
      errorMessage: String(event.error?.message || ""),
      stack: String(event.error?.stack || ""),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    devLog("app", "window_unhandled_rejection", {
      path: window.location.pathname,
      search: window.location.search,
      reason:
        typeof event.reason === "string"
          ? event.reason
          : String(event.reason?.message || event.reason || ""),
      stack: String(event.reason?.stack || ""),
    });
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <HelmetProvider>
      <ClerkProvider
        publishableKey={publishableKey}
        {...clerkConfig}
        onSignUp={logClerkEvent}
        onSignIn={logClerkEvent}
      >
        <Provider store={store}>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </Provider>
      </ClerkProvider>
    </HelmetProvider>
  </React.StrictMode>
);
